// Filtros que se quedan puestos.
//
// Lissette 2026-08-19: "en pólizas, cuando una persona coloca un filtro, deje
// el filtro allí para esa persona y no cambiar hasta que la persona lo cambie".
//
// Un filtro que se borra al salir de la pantalla obliga a rehacerlo veinte
// veces al día, y peor: hace creer que la lista está completa cuando en
// realidad se acaba de reiniciar sola.
//
// Se apoya en el mismo almacén que ya recuerda el tamaño de página: escribe en
// localStorage (lectura síncrona al montar, así el primer pintado ya sale
// filtrado y no parpadea) y se refleja en los uiPrefs del usuario, que
// deskte-full sincroniza con el servidor. Resultado: el filtro sigue a la
// persona entre dispositivos, y es SUYO — no de la agencia.
import { useState, useEffect } from 'react';
import { prefsGet, prefsSet, prefsRemove } from './uiPrefsStore.js';
import { parsearListaGuardada } from './utils.js';

/**
 * Igual que useState, pero recordando el valor bajo `key`.
 *
 * `inicial` se usa cuando no hay nada guardado. Guardar el valor inicial se
 * omite a propósito: así una pantalla que nunca se tocó no llena el almacén de
 * "all", y borrar el filtro (volver al inicial) borra la entrada en vez de
 * dejar basura.
 */
export function useStickyFilter(key, inicial = 'all') {
  const [valor, setValor] = useState(() => {
    try {
      const guardado = prefsGet(key);
      return guardado == null || guardado === '' ? inicial : guardado;
    } catch (_) {
      return inicial;
    }
  });

  useEffect(() => {
    try {
      if (valor === inicial) prefsRemove(key);
      else prefsSet(key, String(valor));
    } catch (_) { /* modo privado / cuota — que no se caiga la pantalla */ }
  }, [key, valor, inicial]);

  return [valor, setValor];
}

/**
 * Igual que useStickyFilter, pero para un filtro de VARIOS valores a la vez.
 *
 * Lissette 2026-08-28: "en el filtro por aseguradora tanto en cliente como en
 * pólizas tener la opción de elegir varias a la vez".
 *
 * **La lista vacía significa "todas"**, no "ninguna". Es la misma idea que el
 * "all" del filtro de un solo valor, pero sin un valor centinela que haya que
 * recordar: si no has elegido nada, no estás filtrando. Así el predicado que lo
 * consume (`coincideConSeleccion`, en utils.js) es una línea y no hay forma de
 * escribir el caso "vacío" al revés y dejar la pantalla en blanco.
 *
 * Se guarda como JSON y no separado por comas porque el nombre de una
 * aseguradora puede llevar una coma dentro ("Blue Cross, Inc.") y partiríamos
 * la lista por la mitad.
 */
export function useStickyFilterList(key) {
  const [lista, setLista] = useState(() => {
    try { return parsearListaGuardada(prefsGet(key)); } catch (_) { return []; }
  });

  useEffect(() => {
    try {
      if (!Array.isArray(lista) || lista.length === 0) prefsRemove(key);
      else prefsSet(key, JSON.stringify(lista));
    } catch (_) { /* modo privado / cuota — que no se caiga la pantalla */ }
  }, [key, lista]);

  return [lista, setLista];
}
